import {
  AutomationDeliveryProvider,
  type AutomationDeliveryProvider as AutomationDeliveryProviderType,
} from "@repo/app-types/entities/automation";
import type {
  AutomationConfigurationProposal,
  AutomationProposalData,
} from "@repo/app-types/entities/automationProposal";
import type { AutomationProviderChannel } from "@repo/app-types/entities/automationReportRouting";
import type { CopilotProposalSummary } from "@repo/app-types/entities/copilotProposal";
import { automationDeliveryProviderLabels } from "@repo/ui/automations/automationPresentation";
import { appPaths } from "../../app/appPaths.js";
import type { CopilotProposalCardContent } from "../copilot/copilotProposalCard.js";
import { buildAutomationProposalEditLocation } from "./automationEditorUrlState.js";
import {
  describeAutomationTrigger,
  describeTrigger,
} from "./automationTriggerSummary.js";

/** What an automation proposal's card in the chat shows and opens. */
export function readAutomationProposalCardContent(
  proposal: CopilotProposalSummary<"automation">,
): CopilotProposalCardContent {
  return {
    resourceType: "automation",
    operation: proposal.kind === "create_automation" ? "create" : "update",
    summary: proposal.data.summary,
    highlights: buildAutomationProposalHighlights(proposal.data),
    review: {
      kind: "open_editor",
      location: buildAutomationProposalEditLocation({
        playbookId: proposal.playbook_id,
        automationId:
          proposal.kind === "create_automation"
            ? undefined
            : proposal.resource.id,
        proposalId: proposal.id,
      }),
    },
    resourceLocation: {
      pathname: appPaths.playbookAutomation.build({
        playbookId: proposal.playbook_id,
        automationId: proposal.resource.id,
      }),
    },
  };
}

/**
 * The card's highlights: when the automation runs, and who receives each
 * result. Both read as values rather than the names of the fields that
 * changed, since the field names repeat what the summary says and the editor
 * shows the change itself. An update that touches neither has no highlights.
 */
function buildAutomationProposalHighlights(
  data: AutomationProposalData,
): readonly string[] {
  if (data.kind === "create_automation") {
    return [
      describeAutomationTrigger({
        template_id: data.template_id,
        settings: data.settings,
      }),
    ];
  }
  return [
    describeProposedSchedule(data.configuration),
    describeProposedAudience(data.configuration),
  ].filter((highlight) => highlight !== undefined);
}

/**
 * When an update's automation runs. Coaching goals go out on a fixed cadence
 * no proposal can move, so only a report carries a schedule to report.
 */
function describeProposedSchedule(
  configuration: AutomationConfigurationProposal,
): string | undefined {
  return configuration.template_family === "report" && configuration.trigger
    ? describeTrigger(configuration.trigger)
    : undefined;
}

/**
 * Who an update's results reach. A channel post is the one delivery that
 * replaces the recipient fields, so the chip names the channels; every other
 * delivery, a direct message as much as an email, still reaches the people the
 * overlay names. Which provider carries the result is not who receives it, so
 * the summary says that rather than the chip.
 */
function describeProposedAudience(
  configuration: AutomationConfigurationProposal,
): string | undefined {
  if (configuration.template_family === "coaching") {
    return describeAudience(
      configuration.recipients ?? configuration.email_recipients,
    );
  }
  const { delivery } = configuration;
  if (
    delivery &&
    delivery.provider !== AutomationDeliveryProvider.EMAIL &&
    delivery.destination.kind === "channel"
  ) {
    return describeChannels(delivery.provider, delivery.destination.channels);
  }
  return describeAudience(
    configuration.people ??
      configuration.recipients ??
      configuration.subscribers,
  );
}

/**
 * The channels a post goes to, named under the provider carrying them, since
 * a channel name alone does not say where it lives. The picker stores each
 * name with its provider's own sigil, so the names read here as they do in
 * the editor; one the picker holds only an id for leaves the whole chip as a
 * count, since a list mixing names with bare ids reads worse than either.
 */
function describeChannels(
  provider: AutomationDeliveryProviderType,
  channels: readonly AutomationProviderChannel[],
): string | undefined {
  if (channels.length === 0) {
    return undefined;
  }
  const label = automationDeliveryProviderLabels[provider];
  const names = channels.flatMap((channel) => channel.name ?? []);
  return names.length === channels.length
    ? `${label} ${names.join(" & ")}`
    : describeCount(channels.length, `${label} channel`, `${label} channels`);
}

/** The audience a recipient field names: its "all" options, then its counts. */
function describeAudience(
  audience:
    | {
        includes_all_teammates?: boolean;
        includes_all_managers?: boolean;
        teammate_ids: readonly string[];
        group_ids?: readonly string[];
      }
    | undefined,
): string | undefined {
  if (!audience) {
    return undefined;
  }
  const parts = [
    audience.includes_all_teammates ? "All teammates" : undefined,
    audience.includes_all_managers ? "All managers" : undefined,
    describeCount(audience.teammate_ids.length, "person", "people"),
    describeCount(audience.group_ids?.length ?? 0, "group", "groups"),
  ].filter((part) => part !== undefined);
  return parts.length > 0 ? parts.join(" & ") : undefined;
}

function describeCount(
  count: number,
  singular: string,
  plural: string,
): string | undefined {
  if (count === 0) {
    return undefined;
  }
  return `${count} ${count === 1 ? singular : plural}`;
}
